import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import {
  ListPageShell, ListPageHeader, ListSearchBar, ListFilterSelect, ListPagination,
} from '../components/ui/ListPageChrome';

const STATUS_OPTIONS = ['Open', 'In Progress', 'Completed', 'Cancelled'];

const STATUS_COLORS = {
  Open:          'bg-[#E8F4FE] text-[#2184F2]',
  'In Progress': 'bg-[#FEF5E7] text-[#B8860B]',
  Completed:     'bg-[#EDF5F0] text-[#4A9E6E]',
  Cancelled:     'bg-[#FDF0EE] text-[#C4574B]',
};

export default function ReworkOrders() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role_name === 'Super Admin';

  const [rows, setRows]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [search, setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading]     = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const limit = 25;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit });
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      const data = await api.get(`/rework?${params}`);
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setSelectedIds(new Set());
      setIsAllSelected(false);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, refreshKey]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / limit) || 1;
  const resetPage = () => setPage(1);

  const handleSelectRow = (id) => {
    if (isAllSelected) {
      const next = new Set();
      rows.forEach(r => {
        if (r.rework_id !== id) {
          next.add(r.rework_id);
        }
      });
      setSelectedIds(next);
      setIsAllSelected(false);
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    }
  };

  const handleSelectAll = () => {
    const pageAllSelected = rows.length > 0 && rows.every(r => selectedIds.has(r.rework_id));
    if (isAllSelected || pageAllSelected) {
      setIsAllSelected(false);
      setSelectedIds(new Set());
    } else {
      setIsAllSelected(true);
      setSelectedIds(new Set());
    }
  };

  const handleBulkDelete = async () => {
    const count = isAllSelected ? total : selectedIds.size;
    if (count === 0) return;
    const msg = `Are you sure you want to delete the ${count} selected rework order(s)?`;
    if (!window.confirm(msg)) return;

    try {
      if (isAllSelected) {
        await api.post('/rework/bulk-delete', {
          all: true,
          search: search || undefined,
          status: statusFilter || undefined
        });
      } else {
        await api.post('/rework/bulk-delete', {
          ids: Array.from(selectedIds)
        });
      }
      setSelectedIds(new Set());
      setIsAllSelected(false);
      
      const newTotal = total - count;
      const newMaxPages = Math.ceil(newTotal / limit) || 1;
      if (page > newMaxPages) {
        setPage(newMaxPages);
      } else {
        setRefreshKey(prev => prev + 1);
      }
    } catch (err) {
      alert(err.message || 'Failed to delete rework orders');
    }
  };

  const displayCount = isAllSelected ? total : selectedIds.size;

  return (
    <ListPageShell>
      <ListPageHeader
        title="Goods Receipt PO – Rework Orders"
        action={
          <div className="flex items-center gap-2">
            {isSuperAdmin && displayCount > 0 && (
              <button type="button" onClick={handleBulkDelete} className="btn-danger">
                Delete Selected ({displayCount})
              </button>
            )}
            <button type="button" onClick={() => navigate('/rework/new')} className="btn-primary">
              + Add New
            </button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-3 items-center">
        <ListSearchBar
          value={search}
          onChange={setSearch}
          onChangeExtra={resetPage}
          placeholder="Search rework number, item, vendor…"
        />
        <ListFilterSelect value={statusFilter} onChange={setStatusFilter} onChangeExtra={resetPage}>
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </ListFilterSelect>
      </div>

      <div className="data-table-wrap overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="table-head">
              {isSuperAdmin && (
                <th className="text-center px-4 py-3 w-12">
                  <input
                    type="checkbox"
                    checked={isAllSelected || (rows.length > 0 && rows.every(r => selectedIds.has(r.rework_id)))}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-sap-blue focus:ring-sap-blue cursor-pointer"
                  />
                </th>
              )}
              {['Rework No.', 'Vendor', 'Item No.', 'Description', 'Qty', 'Unit Price', 'Total', 'Defect Type', 'Status', 'Posting Date'].map((h) => (
                <th key={h} className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isSuperAdmin ? 11 : 10} className="px-4 py-8 text-center text-sap-textLight">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={isSuperAdmin ? 11 : 10} className="px-4 py-8 text-center text-sap-textLight">No rework orders found.</td></tr>
            ) : rows.map((r) => (
              <tr
                key={r.rework_id}
                onClick={() => navigate(`/rework/${r.rework_id}`)}
                className="table-row even:bg-gray-50/50"
              >
                {isSuperAdmin && (
                  <td className="px-4 py-3 text-center w-12" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isAllSelected || selectedIds.has(r.rework_id)}
                      onChange={() => handleSelectRow(r.rework_id)}
                      className="rounded border-gray-300 text-sap-blue focus:ring-sap-blue cursor-pointer"
                    />
                  </td>
                )}
                <td className="px-4 py-3 font-semibold text-sap-blue">{r.rework_number}</td>
                <td className="px-4 py-3">
                  {r.vendor_code && <span className="font-medium">{r.vendor_code} </span>}
                  <span className="text-sap-textLight">{r.vendor_name || '—'}</span>
                </td>
                <td className="px-4 py-3 font-medium">{r.item_no}</td>
                <td className="px-4 py-3 text-sap-textLight max-w-[180px] truncate">{r.item_desc || '—'}</td>
                <td className="px-4 py-3 text-right">{Number(r.rework_qty).toLocaleString()}</td>
                <td className="px-4 py-3 text-right">₹{Number(r.unit_price || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                <td className="px-4 py-3 text-right font-semibold">₹{Number(r.rework_cost || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                <td className="px-4 py-3">{r.defect_type || '—'}</td>
                <td className="px-4 py-3">
                  {r.status === 'Open' ? (
                    <span className="badge-active">{r.status}</span>
                  ) : (
                    <span className={`px-2.5 py-0.5 rounded-lg text-xs font-semibold ${STATUS_COLORS[r.status] || 'bg-slate-100 text-slate-600'}`}>
                      {r.status}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-sap-textLight">{r.posting_date ? r.posting_date.slice(0, 10) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ListPagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />
    </ListPageShell>
  );
}
